import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { showAlert } from 'utils/toastBridge';
import { isAltFunctionKey, isElementVisible } from 'utils/domVisibility';
import { theme } from 'styles/theme';

const ActiveGridContext = createContext(null);

/**
 * 멀티그리드 활성 전환 단축키
 * - F6은 Chromium/브라우저 주소창·포커스 순환과 충돌해 페이지에서 가로채기 불안정
 * - 조회조건 접기(Alt+F1)와 동일하게 Alt+Fn 패턴 사용
 */
export const ACTIVE_GRID_CYCLE_HOTKEY = 'Alt+F2';

/**
 * 멀티그리드 화면: 활성 그리드 + Alt+F2 순환(토글) + 추가/삭제 디스패치
 *
 * ★ 공통 키보드 UX (모든 HiGrid / ActiveGrid 화면 SSOT)
 * - Alt+F2: 등록된 그리드 순환 (이 Provider)
 * - F8/F9: ActionButtonGroup 단축키 → useActiveGridCrudActions → 활성 그리드 onAdd/onDelete
 * - Enter / Shift+Enter: HiGrid 공통 (아래 행 / 위 행, 동일 컬럼)
 * - Tab / Shift+Tab: HiGrid 공통 (좌우 셀)
 *
 * 주의: ActiveGridScope 등록 effect 의존성에 context 객체(ctx) 전체를 넣지 말 것.
 * activeGridId 변경 → ctx 참조 변경 → unregister/register 루프 → 화면 깜빡임.
 */
export function ActiveGridProvider({ children, enableHotkeys = true }) {
  const [activeGridId, setActiveGridIdState] = useState(null);
  const [version, setVersion] = useState(0);
  const gridsRef = useRef(new Map());
  const activeGridIdRef = useRef(null);
  activeGridIdRef.current = activeGridId;

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const registerGrid = useCallback(
    (entry) => {
      if (!entry?.id) return () => {};
      const isNew = !gridsRef.current.has(entry.id);
      const prev = gridsRef.current.get(entry.id);
      gridsRef.current.set(entry.id, {
        order: 100,
        label: entry.id,
        canAdd: true,
        canDelete: true,
        ...prev,
        ...entry,
      });
      if (isNew) {
        setActiveGridIdState((cur) => cur || entry.id);
        bump();
      }
      return () => {
        const cur = gridsRef.current.get(entry.id);
        // 동일 id 재등록 직전 cleanup과 구분: getFocusEl이 바뀌었으면 최신 등록이 덮어쓴 것
        if (cur && cur.getFocusEl !== entry.getFocusEl) return;
        gridsRef.current.delete(entry.id);
        setActiveGridIdState((prevId) => {
          if (prevId !== entry.id) return prevId;
          const list = [...gridsRef.current.values()].sort(
            (a, b) => (a.order ?? 100) - (b.order ?? 100)
          );
          return list[0]?.id || null;
        });
        bump();
      };
    },
    [bump]
  );

  const focusGridEl = useCallback((id) => {
    const g = gridsRef.current.get(id);
    const el = typeof g?.getFocusEl === 'function' ? g.getFocusEl() : null;
    if (!el || typeof el.focus !== 'function') return;
    // Alt+F2 등 명시적 전환: 다른 그리드 셀 편집 중이면 blur 후 대상 포커스
    const ae = document.activeElement;
    if (ae && ae !== el && !el.contains(ae)) {
      const tag = ae.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') {
        try {
          ae.blur();
        } catch {
          /* ignore */
        }
      }
    }
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, []);

  /**
   * @param {string} id
   * @param {{ focus?: boolean }} [opts] - focus:false 이면 활성만 표시(셀 클릭). Alt+F2/헤더 클릭은 기본 focus
   */
  const setActiveGridId = useCallback(
    (id, opts = {}) => {
      if (!id || !gridsRef.current.has(id)) return;
      const prev = activeGridIdRef.current;
      activeGridIdRef.current = id;
      setActiveGridIdState(id);
      // 셀 mousedown/포커스 capture로 이미 활성인 그리드를 다시 mark할 때
      // wrap.focus()가 인라인 에디터 blur → 편집 즉시 종료를 유발함
      if (opts.focus === false) return;
      if (prev === id && opts.focusOnlyIfChanged !== false && opts.focus !== true) {
        // 동일 그리드 재활성화: 명시적 focus:true(Alt+F2 등)가 아니면 포커스 생략
        return;
      }
      requestAnimationFrame(() => focusGridEl(id));
    },
    [focusGridEl]
  );

  const orderedIds = useMemo(() => {
    void version;
    return [...gridsRef.current.values()]
      .sort((a, b) => (a.order ?? 100) - (b.order ?? 100))
      .map((g) => g.id);
  }, [version]);

  const cycleActive = useCallback(
    (dir = 1) => {
      const ids = [...gridsRef.current.values()]
        .sort((a, b) => (a.order ?? 100) - (b.order ?? 100))
        .map((g) => g.id);
      if (!ids.length) return;
      const cur =
        activeGridIdRef.current && ids.includes(activeGridIdRef.current)
          ? activeGridIdRef.current
          : ids[0];
      const idx = Math.max(0, ids.indexOf(cur));
      const next = ids[(idx + dir + ids.length) % ids.length];
      // Alt+F2 순환: 대상 그리드 래퍼로 포커스 이동
      setActiveGridId(next, { focus: true });
    },
    [setActiveGridId]
  );

  const cycleActiveRef = useRef(cycleActive);
  cycleActiveRef.current = cycleActive;

  const getActiveEntry = useCallback(() => {
    void version;
    const id = activeGridIdRef.current;
    if (!id) return null;
    return gridsRef.current.get(id) || null;
  }, [version, activeGridId]);

  const dispatchAdd = useCallback(() => {
    const g = getActiveEntry();
    if (!g || g.canAdd === false) return false;
    g.onAdd?.();
    return true;
  }, [getActiveEntry]);

  const dispatchDelete = useCallback(() => {
    const g = getActiveEntry();
    if (!g || g.canDelete === false) return false;
    g.onDelete?.();
    return true;
  }, [getActiveEntry]);

  useEffect(() => {
    if (!enableHotkeys) return undefined;
    const onKey = (e) => {
      // Alt+F2 — F6(브라우저 주소창) 충돌 회피. CollapsibleSearchPanel Alt+F1과 동일 패턴
      if (!isAltFunctionKey(e, 'F2')) return;
      // 비활성 탭의 Provider는 가로채지 않음 (탭은 display:none 유지·마운트 유지)
      const anyVisible = [...gridsRef.current.values()].some((g) => {
        const el = typeof g?.getFocusEl === 'function' ? g.getFocusEl() : null;
        return isElementVisible(el);
      });
      if (!anyVisible) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
      cycleActiveRef.current(1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
    };
  }, [enableHotkeys]);

  const activeLabel = useMemo(() => {
    void version;
    const g = activeGridId ? gridsRef.current.get(activeGridId) : null;
    return g?.label || '';
  }, [activeGridId, version]);

  const value = useMemo(
    () => ({
      activeGridId,
      activeLabel,
      orderedIds,
      registerGrid,
      setActiveGridId,
      cycleActive,
      dispatchAdd,
      dispatchDelete,
      getActiveEntry,
      cycleHotkey: ACTIVE_GRID_CYCLE_HOTKEY,
    }),
    [
      activeGridId,
      activeLabel,
      orderedIds,
      registerGrid,
      setActiveGridId,
      cycleActive,
      dispatchAdd,
      dispatchDelete,
      getActiveEntry,
    ]
  );

  return <ActiveGridContext.Provider value={value}>{children}</ActiveGridContext.Provider>;
}

export function useActiveGrid() {
  const ctx = useContext(ActiveGridContext);
  if (!ctx) {
    throw new Error('useActiveGrid must be used within ActiveGridProvider');
  }
  return ctx;
}

export function useActiveGridOptional() {
  return useContext(ActiveGridContext);
}

/**
 * 멀티그리드 CRUD 액션바용 — F8/F9를 활성 그리드에 연결하는 공통 훅
 *
 * @param {object} [options]
 * @param {(activeGridId: string|null) => string|null|undefined} [options.guardAdd]
 *   반환 문자열이 있으면 alert 후 중단 (예: 이력 그리드)
 * @param {(activeGridId: string|null) => string|null|undefined} [options.guardDelete]
 *
 * @example
 * const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
 *   guardAdd: (id) => (id === HIST_ID ? '이력은 조회 전용입니다.' : null),
 *   guardDelete: (id) => (id === HIST_ID ? '이력은 삭제할 수 없습니다.' : null),
 * });
 * // ActionButtonGroup: onClick: handleActiveAdd, hotkey: 'F8'
 */
export function useActiveGridCrudActions(options = {}) {
  const { dispatchAdd, dispatchDelete, activeLabel, activeGridId } = useActiveGrid();
  const { guardAdd, guardDelete } = options;

  const handleActiveAdd = useCallback(() => {
    const block = typeof guardAdd === 'function' ? guardAdd(activeGridId) : null;
    if (block) {
      showAlert(block, 'warning');
      return;
    }
    if (!dispatchAdd()) {
      showAlert(`추가할 그리드를 선택하세요. (${ACTIVE_GRID_CYCLE_HOTKEY})`, 'warning');
    }
  }, [activeGridId, dispatchAdd, guardAdd]);

  const handleActiveDelete = useCallback(() => {
    const block = typeof guardDelete === 'function' ? guardDelete(activeGridId) : null;
    if (block) {
      showAlert(block, 'warning');
      return;
    }
    if (!dispatchDelete()) {
      showAlert(`삭제할 그리드를 선택하세요. (${ACTIVE_GRID_CYCLE_HOTKEY})`, 'warning');
    }
  }, [activeGridId, dispatchDelete, guardDelete]);

  return {
    handleActiveAdd,
    handleActiveDelete,
    activeLabel,
    activeGridId,
    dispatchAdd,
    dispatchDelete,
    cycleHotkey: ACTIVE_GRID_CYCLE_HOTKEY,
  };
}

/** 활성 그리드 헤더 색 — CSS 변수(런타임 테마) 우선, theme fallback */
export const ACTIVE_GRID_HEADER_BG = `var(--erp-color-grid-header-active-bg, ${theme.colors.grid.headerActiveBg})`;
export const ACTIVE_GRID_HEADER_FG = `var(--erp-color-grid-header-active-fg, ${theme.colors.grid.headerActiveFg})`;
export const INACTIVE_GRID_HEADER_BG = `var(--erp-color-bg-medium, ${theme.colors.gridInactive.headerBg})`;
export const INACTIVE_GRID_HEADER_FG = `var(--erp-color-text, ${theme.colors.gridInactive.headerFg})`;

/**
 * 그리드 패널 타이틀 바 (활성 색 없음 — 컬럼 헤더는 HiGrid headerActive 사용)
 * 클릭 시 해당 그리드 활성화
 */
export function ActiveGridHeader({ gridId, children, extra, style }) {
  const ctx = useActiveGridOptional();
  const active = ctx?.activeGridId === gridId;

  return (
    <div
      role="button"
      tabIndex={0}
      data-active-grid-header={active ? 'Y' : 'N'}
      aria-pressed={active}
      onMouseDown={(e) => {
        e.preventDefault();
        if (gridId) ctx?.setActiveGridId(gridId, { focus: true });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (gridId) ctx?.setActiveGridId(gridId, { focus: true });
        }
      }}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        padding: '8px 12px',
        fontWeight: 700,
        fontSize: '0.875rem',
        cursor: 'pointer',
        userSelect: 'none',
        background: INACTIVE_GRID_HEADER_BG,
        color: INACTIVE_GRID_HEADER_FG,
        borderBottom: `1px solid var(--erp-color-border, ${theme.colors.border})`,
        ...style,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {children}
        {active ? (
          <span
            style={{
              flexShrink: 0,
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              padding: '2px 8px',
              borderRadius: 999,
              background: ACTIVE_GRID_HEADER_BG,
              color: ACTIVE_GRID_HEADER_FG,
            }}
          >
            선택됨
          </span>
        ) : null}
      </span>
      {extra != null ? (
        <span
          style={{
            fontWeight: 500,
            fontSize: '0.75rem',
            color: `var(--erp-color-text-secondary, ${theme.colors.text.secondary})`,
            marginLeft: 12,
            flexShrink: 0,
          }}
        >
          {extra}
        </span>
      ) : null}
    </div>
  );
}

/**
 * 그리드 영역 포커스 시 활성 등록 + 시각 강조
 */
export function ActiveGridScope({
  gridId,
  label,
  order = 100,
  onAdd,
  onDelete,
  canAdd = true,
  canDelete = true,
  children,
  style,
}) {
  const ctx = useActiveGridOptional();
  const registerGrid = ctx?.registerGrid;
  const setActiveGridId = ctx?.setActiveGridId;
  const activeGridId = ctx?.activeGridId;
  const wrapRef = useRef(null);
  const onAddRef = useRef(onAdd);
  const onDeleteRef = useRef(onDelete);
  onAddRef.current = onAdd;
  onDeleteRef.current = onDelete;

  // registerGrid만 의존 — ctx 전체 의존 시 active 변경마다 unregister 루프 발생
  useEffect(() => {
    if (!registerGrid || !gridId) return undefined;
    const getFocusEl = () => wrapRef.current;
    return registerGrid({
      id: gridId,
      label: label || gridId,
      order,
      canAdd,
      canDelete,
      onAdd: () => onAddRef.current?.(),
      onDelete: () => onDeleteRef.current?.(),
      getFocusEl,
    });
  }, [registerGrid, gridId, label, order, canAdd, canDelete]);

  if (!ctx) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, ...style }}>
        {children}
      </div>
    );
  }

  const active = activeGridId === gridId;
  // 셀 클릭: mousedown에서 활성. wrap 프로그래밍 포커스(셀 저장 후 focusGrid)는 타 그리드 활성을 뺏지 않음
  const mark = () => setActiveGridId?.(gridId, { focus: false });
  const onFocusCapture = (e) => {
    // wrap 자체로 온 포커스 = 셀 저장 후 gridWrap.focus() → 무시 (디테일 클릭이 마스터로 되돌아가는 원인)
    if (e.target === wrapRef.current) return;
    mark();
  };

  return (
    <div
      ref={wrapRef}
      tabIndex={-1}
      role="region"
      aria-label={label || gridId}
      data-active-grid={active ? 'Y' : 'N'}
      onMouseDownCapture={mark}
      onFocusCapture={onFocusCapture}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        flex: 1,
        outline: 'none',
        borderRadius: 4,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
